import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faClock,
	faChalkboardTeacher,
	faCalendar,
	faBook,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../common/Modal";
import SearchInput from "../../common/SearchInput";
import ToggleSwitch from "../../common/ToggleSwitch";
import Notification from "../../common/Notification";
import Button from "../../common/Button";
import { useClassLogForm } from "./hooks/useClassLogForm";
import { CLASS_PERIODS } from "../../../config";
import styles from "./ClassLogFormModal.module.css";

const ClassLogFormModal = ({
	isOpen,
	onClose,
	departmentId,
	subjectId,
	disableDayAndPeriodSelection = false,
	externalDate,
	externalPeriod,
	subjects = [],
	onSuccess,
	substituteMode = false, // NEW: Add substitute mode prop
	substituteSubjectId = null, // NEW: Add substitute subject ID
}) => {
	const {
		isNewUiMode,
		weekDays,
		selectedDay,
		setSelectedDay,
		classHour,
		setClassHour,
		lectureTitle,
		setLectureTitle,
		classSequence,
		setClassSequence,
		absentStudents,
		setAbsentStudents,
		studentOptions,
		isLoading,
		teacherSubjects,
		selectedSubject,
		setSelectedSubject,
		notifications,
		removeNotification,
		handleSubmit,
	} = useClassLogForm({
		departmentId,
		subjectId,
		disableDayAndPeriodSelection,
		externalDate,
		externalPeriod,
		subjects,
		onSuccess,
		onClose,
		substituteMode, // NEW: Pass substitute mode
		substituteSubjectId, // NEW: Pass substitute subject ID
	});

	const [searchTerm, setSearchTerm] = useState("");

	// Filter students based on search term AND sort alphabetically
	const filteredStudents = studentOptions
		.filter((student) =>
			student.label.toLowerCase().includes(searchTerm.toLowerCase())
		)
		.sort((a, b) => a.label.localeCompare(b.label));

	// Check if a student is marked as absent
	const isStudentAbsent = (studentId) => {
		return absentStudents.some((s) => s.value === studentId);
	};

	// Toggle a student's absence status
	const toggleStudentAbsence = (student) => {
		if (isStudentAbsent(student.value)) {
			setAbsentStudents(
				absentStudents.filter((s) => s.value !== student.value)
			);
		} else {
			setAbsentStudents([...absentStudents, student]);
		}
	};

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isLoading}>
				Cancel
			</Button>
			<Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
				{isLoading ? "Saving..." : "Log Class"}
			</Button>
		</>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Log a Class"
			size="large"
			footer={footer}
			loading={isLoading}
			className={styles.classLogModal}
		>
			{/* Notifications */}
			{notifications.length > 0 && (
				<div className={styles.notifications}>
					{notifications.map((note) => (
						<Notification
							key={note.id}
							type={note.type}
							message={note.description}
							onClose={() => removeNotification(note.id)}
						/>
					))}
				</div>
			)}

			{/* Two-column layout */}
			<div className={styles.modalContent}>
				{/* Left column - Class Information */}
				<div className={styles.classInfoColumn}>
					<div className={styles.sectionHeader}>
						<h3>Class Information</h3>
					</div>

					<div className={styles.formFields}>
						{!isNewUiMode && (
							<>
								<div className={styles.formGroup}>
									<label>
										<FontAwesomeIcon
											icon={faCalendar}
											className={styles.fieldIcon}
										/>
										Day of the Week
									</label>
									<select
										className={styles.select}
										value={selectedDay}
										onChange={(e) => setSelectedDay(e.target.value)}
									>
										<option value="">Select a day</option>
										{weekDays.map((day) => (
											<option key={day.value} value={day.value}>
												{day.label}
											</option>
										))}
									</select>
								</div>

								<div className={styles.formGroup}>
									<label>
										<FontAwesomeIcon
											icon={faClock}
											className={styles.fieldIcon}
										/>
										Period
									</label>
									<select
										className={styles.select}
										value={classHour}
										onChange={(e) => setClassHour(e.target.value)}
									>
										<option value="">Select a period</option>
										{CLASS_PERIODS.map((period) => (
											<option key={period.value} value={period.value}>
												{period.label}
											</option>
										))}
									</select>
								</div>
							</>
						)}

						{isNewUiMode && (
							<div className={styles.formGroup}>
								<label>
									<FontAwesomeIcon icon={faBook} className={styles.fieldIcon} />
									Subject
								</label>
								<select
									className={styles.select}
									value={selectedSubject}
									onChange={(e) => setSelectedSubject(e.target.value)}
								>
									<option value="">Select a subject</option>
									{teacherSubjects.map((subject) => (
										<option key={subject.value} value={subject.value}>
											{subject.label}
										</option>
									))}
								</select>
							</div>
						)}

						<div className={styles.formGroup}>
							<label>
								<FontAwesomeIcon
									icon={faChalkboardTeacher}
									className={styles.fieldIcon}
								/>
								Lecture Title
							</label>
							<input
								type="text"
								className={styles.textInput}
								placeholder="Enter today's lecture title"
								value={lectureTitle}
								onChange={(e) => setLectureTitle(e.target.value)}
							/>
						</div>

						<div className={styles.summary}>
							<div className={styles.attendanceSummary}>
								<h4>Attendance Summary</h4>
								<div className={styles.summaryStats}>
									<div className={styles.stat}>
										<span className={styles.statLabel}>Present:</span>
										<span className={styles.statValue}>
											{studentOptions.length - absentStudents.length}
										</span>
									</div>
									<div className={styles.stat}>
										<span className={styles.statLabel}>Absent:</span>
										<span
											className={`${styles.statValue} ${styles.absentCount}`}
										>
											{absentStudents.length}
										</span>
									</div>
									<div className={styles.stat}>
										<span className={styles.statLabel}>Total:</span>
										<span className={styles.statValue}>
											{studentOptions.length}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Right column - Student Attendance */}
				<div className={styles.attendanceColumn}>
					<div className={styles.sectionHeader}>
						<h3>Student Attendance</h3>
					</div>

					<div className={styles.studentSearch}>
						<SearchInput
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder="Search students..."
						/>
					</div>

					<div className={styles.studentsList}>
						{filteredStudents.length > 0 ? (
							filteredStudents.map((student) => (
								<div
									key={student.value}
									className={`${styles.studentItem} ${
										isStudentAbsent(student.value) ? styles.absent : ""
									}`}
								>
									<span className={styles.studentName}>{student.label}</span>
									<ToggleSwitch
										checked={isStudentAbsent(student.value)}
										onChange={() => toggleStudentAbsence(student)}
										label={
											isStudentAbsent(student.value) ? "Absent" : "Present"
										}
										variant={
											isStudentAbsent(student.value) ? "danger" : "default"
										}
									/>
								</div>
							))
						) : (
							<div className={styles.noResults}>
								<p>No students found matching your search criteria.</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default ClassLogFormModal;
